import React, { useEffect, useMemo, useState } from 'react';

const stopped = { status: 'stopped', error: 'Stopped waiting. The private request may still finish before its two-minute deadline. Resume to check the same attempt.' };

// Owned by settings, not the conditionally mounted step. Authority changes get
// an empty session immediately; navigation cancels polling, not the attempt.
export function useExternalCoachConnectionTest(backendService, authority, visible) {
  const session = useMemo(() => ({ backendService, authority, attempt: { current: null }, running: { current: null } }), [backendService, authority]);
  const { attempt, running } = session;
  const [result, setResult] = useState(null);
  const state = result?.session === session ? result.state : { status: 'idle' };
  const setState = next => setResult({ session, state: next });
  useEffect(() => () => {
    const controller = running.current;
    running.current = null;
    controller?.abort();
    setResult(previous => previous?.session === session && previous.state.status === 'pending' ? { session, state: stopped } : previous);
  }, [session, running, visible]);
  const stop = () => {
    running.current?.abort(); running.current = null;
    setState(stopped);
  };
  const start = async (fresh = false) => {
    if (!visible || running.current) return;
    const current = fresh || !attempt.current ? { key: `probe-${crypto.randomUUID()}` } : attempt.current;
    // Keep a known request until the server accepts its replacement. A 429
    // must leave Resume pointing at the original, not the rejected key.
    if (!attempt.current) attempt.current = current;
    const controller = new AbortController();
    running.current = controller;
    const active = () => running.current === controller && !controller.signal.aborted;
    setState({ status: 'pending', phase: 'Sending private test…' });
    const deadline = setTimeout(() => controller.abort(), 120000);
    // Race the entire promise, including auth acquisition, not only fetch.
    const bounded = operation => new Promise((resolve, reject) => {
      const abort = () => reject(new Error('Test timed out. Check your external worker status and logs, then retry.'));
      if (controller.signal.aborted) return abort();
      controller.signal.addEventListener('abort', abort, { once: true });
      Promise.resolve().then(operation).then(resolve, reject).finally(() => controller.signal.removeEventListener('abort', abort));
    });
    try {
      if (!current.id) {
        const created = await bounded(() => backendService.createExternalCoachTest(current.key, controller.signal));
        if (!active()) return;
        if (!created.request?.id) throw new Error('The server did not return a test request. Retry this attempt.');
        current.id = created.request.id;
        attempt.current = current;
      }
      while (active()) {
        const result = await bounded(() => backendService.fetchExternalCoachTest(current.id, controller.signal));
        if (!active()) return;
        if (result.request?.id !== current.id) throw new Error('Test response did not match this attempt. No connection verified.');
        if (result.request.status === 'completed') {
          if (result.verified !== true || result.reply?.source !== 'external_agent' || result.reply.request_id !== current.id || !result.reply.text?.trim()) {
            throw new Error('No verified external reply was persisted for this attempt.');
          }
          setState({ status: 'success', reply: result.reply });
          return;
        }
        if (['failed', 'timedout'].includes(result.request.status)) {
          setState({ status: 'failed', error: 'No external reply received. Check your external worker status, credential expiry and private logs, then start a new test.' });
          return;
        }
        setState({ status: 'pending', phase: `Waiting for external Coach · ${result.request.status}` });
        await bounded(() => new Promise(resolve => {
          const finish = () => { clearTimeout(timer); controller.signal.removeEventListener('abort', finish); resolve(); };
          const timer = setTimeout(finish, 2000);
          controller.signal.addEventListener('abort', finish, { once: true });
        }));
      }
    } catch (error) {
      if (running.current === controller) setState({ status: 'error', error: error.message || 'Test failed. Retry this attempt.' });
    } finally {
      clearTimeout(deadline);
      if (running.current === controller) running.current = null;
    }
  };
  return { state, start, stop };
}

// Only canonical, request-attributed responses establish verification.
export default function ExternalCoachConnectionTest({ test, enabled, onEnable }) {
  const { state, start, stop } = test;
  return <section aria-label="Live external Coach test" className="external-coach-test">
    <p>Send a private greeting through your configured external worker and show its reply here. The test and reply are stored privately, not added to your Coach conversation or the dojo feed. No health history is sent and no plans or proposals can be changed.</p>
    <p>Allow up to two minutes. A saved credential or “last connected” timestamp is not a verified reply. Hosted fallback is never used for this test.</p>
    {!enabled ? <><p>External routing is off. Enable it in step 4 before testing.</p><button type="button" onClick={onEnable}>Go to Enable routing</button></> : <>
      {state.status === 'pending' ? <><p role="status">{state.phase}</p><button type="button" onClick={stop}>Stop waiting</button></> : <button className="external-coach-primary" type="button" onClick={() => start(['success', 'failed'].includes(state.status))}>{state.status === 'idle' ? 'Test connection' : ['success', 'failed'].includes(state.status) ? 'Run new test' : 'Resume test'}</button>}
      {state.error && <p role="alert">{state.error}</p>}
      {['error', 'stopped'].includes(state.status) && <button type="button" onClick={() => start(true)}>Run new test</button>}
      {state.status === 'success' && <div role="status" className="external-coach-test-result ph-no-capture ph-mask" data-private="true"><strong>Verified external reply · {state.reply.agent_name}</strong><p>{state.reply.text}</p><small>This attempt completed through the external agent. Future availability depends on your worker staying online.</small></div>}
    </>}
  </section>;
}
